import React, { FC, forwardRef, useEffect, useState } from "react";
import { Dialog, DialogActions, DialogContent, IconButton, TextField } from "@mui/material";
import { Autocomplete } from "@mui/material";
import { Close, DeleteOutlined, Done } from "@mui/icons-material";
import makeStyles from "@mui/styles/makeStyles";
import dynamic from "next/dynamic";
import { Controller, ControllerRenderProps, useForm } from "react-hook-form";
import { useMetadataByCategory } from "components/summary/hooks";
import { UseFormStateReturn } from "react-hook-form/dist/types";
import MButton from "components/momentum/MButton";
import { toast } from "react-toastify";
interface Option {
  value: string;
  name: string;
  painPoints: string;
}

const useStyles = makeStyles(() => ({
  listbox: {
    '& > li[aria-selected="true"]': {
      backgroundColor: "#EDFBFE",
    },
  },
  selectTitle: {
    marginLeft: 12,
    color: "#535759",
    fontWeight: 500,
    fontSize: 14,
    marginRight: 8,
  },
  option: {
    minHeight: 1000,
  },
  titleContainer: {
    display: "flex",
    justifyContent: "space-between",
    padding: "16px 24px",
    alignItems: "center",
  },
  title: {
    fontSize: 16,
    color: "#171B1F",
    fontWeight: 500,
    display: "flex",
    lineHeight: "24px",
  },
  field: {
    marginTop: 24,
  },
  fieldLabel: {
    marginBottom: 8,
    fontSize: 12,
    color: "#535759",
    fontWeight: 500,
  },
  textField: {
    width: 200,
  },
}));

const QVRDialogPainPoints: FC<
  ControllerRenderProps & UseFormStateReturn<any> & { clearErrors: any }
> = (props) => {
  const classes = useStyles();
  const { data: painPoints } = useMetadataByCategory("QVR_PAIN_POINTS");
  const [options, setOptions] = useState<Option[]>([]);

  const [value, setValue] = useState<Option[]>([]);
  const [open, setOpen] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);

  useEffect(() => {
    if (painPoints) {
      const otherOptions = props.value?.filter((item: string) => item?.startsWith?.("Other"));
      const options: Option[] = [{ name: "None", value: "null", painPoints: "" }];
      painPoints?.reduce((prv, cur) => {
        const [painPoint, name] = cur.value.split(" - ");
        prv.push({
          name,
          value: cur.value,
          painPoints: painPoint,
        });
        return prv;
      }, options);
      otherOptions?.reduce((prv: any, cur: any) => {
        const [painPoint, name] = cur.split(" - ");
        prv.push({
          name,
          value: cur,
          painPoints: painPoint,
        });
        return prv;
      }, options);
      setOptions([...options]);
    }
  }, [painPoints]);

  useEffect(() => {
    if (props.value && options.length && isFirstRender) {
      const intersection = options.filter((v) => props.value.includes(v.value));
      setValue(intersection);
      setIsFirstRender(false);
    }
  }, [props.value, options.length]);
  const onChange = (_e: any, value: Option[]) => {
    setIsFirstRender(false);
    const nullOptionIndex = value.findIndex((x) => x.value === "null");
    // when select none, clear other
    if (nullOptionIndex === value.length - 1 && nullOptionIndex !== -1) {
      setValue([value[nullOptionIndex]]);
      props.onChange([value[nullOptionIndex]]);
      // when select none than select other
    } else if (nullOptionIndex === 0) {
      setValue(value.slice(1));
      props.onChange(value.slice(1));
    } else {
      setValue(value);
      props.onChange(value.map((x) => x.value));
    }
  };
  const onOpen = () => setOpen(true);
  const { handleSubmit, control } = useForm();
  const onSubmit = (submitData: { [key: string]: string & string[] }) => {
    const { painPoint } = submitData;
    const newValue = `Other - ${painPoint}`;
    if (options.filter((x) => x.value === newValue).length > 0) {
      toast.error("Same option already added");
      return;
    }
    const newOption = {
      name: painPoint,
      value: newValue,
      painPoints: "Other",
    };
    setOptions((pre) => [...pre, newOption]);
    onChange(null, [...value, newOption]);
    // setValue((prv) => [...prv, newOption]);
    setOpen(false);
    props.clearErrors(props.name);
    toast.success("Add Pain Point success!");
  };
  const onError = (errors: { [key: string]: any }) => {
    console.log(errors);
    if (Object.keys(errors).length) {
      toast.error("Please correct the field", {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };
  const ListboxComponent = forwardRef<HTMLUListElement>(({ children, ...rest }, ref) => {
    (children as any[]).push(
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          margin: "8px 4px 0",
        }}
        key={"button"}
        role={"option"}
        // onClick={(e) => e.stopPropagation()}
      >
        <MButton
          buttonType="blue-reverse"
          buttonSize={28}
          onClick={onOpen}
        >
          Add Other
        </MButton>
      </div>
    );
    return (
      <ul
        {...rest}
        ref={ref}
      >
        {children}
      </ul>
    );
  });

  const onClick = (option: Option) => {
    setValue((x) => x.filter((v) => v.value !== option.value));
    setOptions((x) => x.filter((v) => v.value !== option.value));
  };

  return (
    <>
      <div style={{ display: "flex", alignItems: "center" }}>
        <Autocomplete
          classes={{
            listbox: classes.listbox,
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              inputProps={{
                ...params.inputProps,
              }}
              InputProps={{
                ...params.InputProps,
              }}
              error={!!props.errors?.[props.name]}
              helperText={props.errors?.[props.name]?.message}
              size={"small"}
            />
          )}
          filterOptions={(o, s) =>
            o.filter(
              (x) =>
                x.value?.toLowerCase().includes(s.inputValue?.toLowerCase()) ||
                x.painPoints?.toLowerCase().includes(s.inputValue?.toLowerCase())
            )
          }
          disableCloseOnSelect={true}
          options={options}
          ListboxComponent={ListboxComponent as any}
          loading={options.length === 0}
          loadingText={"Loading..."}
          fullWidth={true}
          isOptionEqualToValue={(o, v) => o.value === v?.value}
          getOptionLabel={(o) => o.name}
          multiple={true}
          renderOption={(props, option, { inputValue, selected }) => {
            const regex = new RegExp(inputValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "ig");
            const spanText = option.name.replace(
              regex,
              `<span style="background-color:#FFE399; font-size: 14px">$&</span>`
            );
            const painPoints = option.painPoints?.replace(
              regex,
              `<span style="background-color:#FFE399; font-size: 14px">$&</span>`
            );
            return (
              <li {...props}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: 14,
                        color: "#333",
                        fontWeight: 700,
                      }}
                      dangerouslySetInnerHTML={{ __html: painPoints }}
                    />
                    {option.painPoints && <>&nbsp; - &nbsp;</>}
                    <div
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: 14,
                        color: "#333",
                      }}
                      dangerouslySetInnerHTML={{ __html: spanText }}
                    />
                    {option.value?.startsWith?.("Other") ? (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={onClick.bind(this, option)}
                      >
                        <DeleteOutlined />
                      </IconButton>
                    ) : null}
                  </div>
                  {selected ? <Done style={{ color: "#007AA3" }} /> : null}
                </div>
              </li>
            );
          }}
          size={"small"}
          onChange={onChange}
          value={value}
        />
      </div>
      <Dialog
        open={open}
        scroll={"body"}
        fullWidth={true}
        maxWidth={"md"}
        onClose={() => setOpen(false)}
      >
        <form style={{ height: "100%" }}>
          <div className={classes.titleContainer}>
            <div>
              <p className={classes.title}>Add Other</p>
            </div>
            <IconButton
              onClick={() => setOpen(false)}
              size="small"
            >
              <Close style={{ color: "#121212", fillOpacity: 0.6 }} />
            </IconButton>
          </div>
          <DialogContent
            dividers={true}
            style={{
              paddingTop: 0,
            }}
          >
            <div className={classes.field}>
              <Controller
                rules={{
                  required: {
                    message: "This field is required!",
                    value: true,
                  },
                  maxLength: { value: 100, message: "Maximum 100 characters" },
                }}
                control={control}
                name={"painPoint"}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                    }}
                    multiline={true}
                    rows={4}
                    variant="outlined"
                    fullWidth={true}
                    placeholder="Please enter"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>
          </DialogContent>
          <DialogActions>
            <MButton
              type="submit"
              buttonType="blue"
              buttonSize={36}
              onClick={handleSubmit(onSubmit, onError)}
              // onClick={onSubmitQVR}
            >
              Submit
            </MButton>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default dynamic(() => Promise.resolve(QVRDialogPainPoints), {
  ssr: false,
});
